// DataPreviewTable.tsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
    Box, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper,
    IconButton, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    MenuItem, Chip, Tooltip, TablePagination,
    Autocomplete
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import type { ParsedFileResult } from '../../../../../../utils/priceListUpload/types';
import type { ColumnMapping } from '../../../../../../utils/priceListUpload/buildEntries';
import { buildEntries } from '../../../../../../utils/priceListUpload/buildEntries';

const schemaFields = [
    { key: '', label: 'Store in JSON fields' },
    { key: 'partNo', label: 'Part Number' },
    { key: 'productName', label: 'Product Name' },
    { key: 'vendorName', label: 'Vendor Name' },
    { key: 'productBrand', label: 'Product Brand' },
    { key: 'carBrand', label: 'Car Brand' },
    { key: 'carModel', label: 'Car Model' },
    { key: 'mrp', label: 'MRP' },
    { key: 'purchasePrice', label: 'Purchase Price' },
    { key: 'descriptionText', label: 'Description (text)' },
    { key: '__extra', label: 'Store in description.jsonFields' },
    { key: 'status', label: 'Status' },
];

interface Props {
    parsed: ParsedFileResult;
    mapping: ColumnMapping;
    onMappingChange: (m: ColumnMapping) => void;
    onMappedRowsChange: (mappedRows: any[]) => void;
    onDeleteColumn?: (h: string) => void;
    onParsedUpdate?: (updatedParsed: ParsedFileResult) => void;
    showMapping?: boolean;
    selectedVendorName?: string;
}

const DataPreviewTable: React.FC<Props> = ({
    parsed,
    mapping,
    onMappingChange,
    onMappedRowsChange,
    onDeleteColumn,
    onParsedUpdate,
    showMapping = true,
    selectedVendorName = ''
}) => {
    const [visibleHeaders, setVisibleHeaders] = useState<string[]>([]);
    const [editOpen, setEditOpen] = useState(false);
    const [editRowIndex, setEditRowIndex] = useState<number | null>(null);
    const [editRowData, setEditRowData] = useState<Record<string, any>>({});
    const [mapColumnDialog, setMapColumnDialog] = useState<{ open: boolean; header: string }>({ open: false, header: '' });
    const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; type: 'row' | 'column'; index: number | string; data?: any }>({ open: false, type: 'row', index: -1 });

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Initialize visible headers safely
    useEffect(() => {
        if (parsed?.headers) {
            setVisibleHeaders([...parsed.headers]);
        }
    }, [parsed?.headers]);

    // Get currently used schema fields (excluding empty, __extra, and descriptionText)
    const usedSchemaFields = useMemo(() => {
        return new Set(
            Object.values(mapping).filter(
                (v) => v !== '' && v !== '__extra' && v !== 'descriptionText'
            )
        );
    }, [mapping]);

    // Build full mapped objects using stable column indices
    const fullMapped = useMemo(() => {
        if (!parsed) return [];
        const entries = buildEntries(parsed, mapping);

        // Override vendorName in entries with selectedVendorName if available
        if (selectedVendorName) {
            entries.forEach(entry => {
                entry.vendorName = selectedVendorName;
            });
        }

        onMappedRowsChange(entries);
        return entries;
    }, [parsed, mapping, selectedVendorName, onMappedRowsChange]);

    // Pagination calculations
    const paginatedRows = useMemo(() => {
        return fullMapped.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [fullMapped, page, rowsPerPage]);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDeleteRow = useCallback((idx: number) => {
        if (!parsed) return;

        // Calculate the actual index in the original data
        const actualIndex = page * rowsPerPage + idx;

        const updatedParsed = {
            ...parsed,
            headers: [...parsed.headers],
            rows: parsed.rows.filter((_, index) => index !== actualIndex),
            totalRows: parsed.rows.length - 1
        };

        if (onParsedUpdate) {
            onParsedUpdate(updatedParsed);
        }

        setVisibleHeaders([...updatedParsed.headers]);
        onMappedRowsChange(buildEntries(updatedParsed, mapping));
        setDeleteConfirm({ open: false, type: 'row', index: -1 });

        // Reset to first page if we deleted the last row on current page
        if (paginatedRows.length === 1 && page > 0) {
            setPage(page - 1);
        }
    }, [parsed, mapping, onMappedRowsChange, onParsedUpdate, page, rowsPerPage, paginatedRows]);

    const handleDeleteColumn = useCallback((colHeader: string) => {
        if (onDeleteColumn) {
            onDeleteColumn(colHeader);
            setDeleteConfirm({ open: false, type: 'column', index: '' });
            return;
        }

        if (!parsed) return;
        const colIndex = parsed.headers.indexOf(colHeader);
        if (colIndex < 0) return;

        const updatedHeaders = parsed.headers.filter(h => h !== colHeader);
        const updatedRows = parsed.rows.map(row => {
            const newRow = [...row];
            newRow.splice(colIndex, 1);
            return newRow;
        });

        const updatedParsed = {
            ...parsed,
            headers: updatedHeaders,
            rows: updatedRows
        };

        const newMapping = { ...mapping };
        delete newMapping[colHeader];

        onMappingChange(newMapping);
        setVisibleHeaders([...updatedHeaders]);

        if (onParsedUpdate) {
            onParsedUpdate(updatedParsed);
        }

        onMappedRowsChange(buildEntries(updatedParsed, newMapping));
        setDeleteConfirm({ open: false, type: 'column', index: '' });
    }, [parsed, mapping, onMappingChange, onMappedRowsChange, onParsedUpdate, onDeleteColumn]);

    const handleColumnMapping = useCallback((header: string, mappedField: string) => {
        onMappingChange({ ...mapping, [header]: mappedField });
        setMapColumnDialog({ open: false, header: '' });
    }, [mapping, onMappingChange]);

    const openEdit = useCallback((idx: number) => {
        // Calculate the actual index in the original data
        const actualIndex = page * rowsPerPage + idx;
        setEditRowIndex(actualIndex);
        setEditRowData(fullMapped[actualIndex] ?? {});
        setEditOpen(true);
    }, [fullMapped, page, rowsPerPage]);

    const saveEdit = useCallback(() => {
        if (editRowIndex === null || !onParsedUpdate || !parsed) return;

        const updatedParsed = {
            ...parsed,
            headers: [...parsed.headers],
            rows: parsed.rows.map((row, index) =>
                index === editRowIndex ? [...row] : [...row]
            )
        };

        const rowArr = updatedParsed.rows[editRowIndex];

        parsed.headers.forEach((header, colIndex) => {
            const mapped = mapping[header];
            let newValue = '';

            if (!mapped || mapped === '' || mapped === '__extra') {
                newValue = editRowData.description?.jsonFields?.[header] ?? '';
            } else if (mapped === 'descriptionText') {
                newValue = editRowData.description?.text ?? '';
            } else if (mapped === 'vendorName') {
                // Use selected vendor name if available, otherwise use edited value
                newValue = selectedVendorName || editRowData.vendorName || '';
            } else {
                newValue = editRowData[mapped] ?? '';
            }

            if (colIndex < rowArr.length) {
                rowArr[colIndex] = String(newValue);
            }
        });

        setEditOpen(false);
        setEditRowIndex(null);
        setEditRowData({});
        setVisibleHeaders([...updatedParsed.headers]);
        onParsedUpdate(updatedParsed);
    }, [editRowIndex, editRowData, parsed, mapping, onParsedUpdate, selectedVendorName]);

    const getMappedFieldLabel = useCallback((header: string) => {
        const mapped = mapping[header];
        const field = schemaFields.find(f => f.key === mapped);
        return field ? field.label : 'Not mapped';
    }, [mapping]);

    const getMappingColor = useCallback((header: string) => {
        const mapped = mapping[header];
        if (!mapped || mapped === '') return 'default';
        if (mapped === '__extra') return 'secondary';
        return 'primary';
    }, [mapping]);

    const isFieldDisabled = useCallback((fieldKey: string, currentHeader: string) => {
        if (fieldKey === '' || fieldKey === '__extra' || fieldKey === 'descriptionText') {
            return false; // These fields can be selected multiple times
        }

        // Check if this field is already used by another column
        const isUsed = usedSchemaFields.has(fieldKey);
        const isCurrentMapping = mapping[currentHeader] === fieldKey;

        return isUsed && !isCurrentMapping;
    }, [usedSchemaFields, mapping]);

    const getFieldDescription = useCallback((fieldKey: string) => {
        if (fieldKey === '') {
            return 'Store this column in description.jsonFields';
        }
        if (fieldKey === '__extra') {
            return 'Store this column in description.jsonFields with column name as key';
        }
        if (fieldKey === 'descriptionText') {
            return 'Use this column as the main description text';
        }
        return `Map to ${schemaFields.find(f => f.key === fieldKey)?.label}`;
    }, []);

    const truncateText = (text: string, maxLength: number = 30) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    if (!parsed) {
        return (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">
                    No data available
                </Typography>
            </Paper>
        );
    }

    return (
        <Box>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Data Preview - Showing {paginatedRows.length} of {parsed.totalRows} rows
            </Typography>

            <Paper>
                <TableContainer sx={{ maxHeight: 480 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: 50 }}>#</TableCell>

                                {visibleHeaders.map((h) => (
                                    <TableCell key={h} sx={{ maxWidth: 200, minWidth: 120 }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Tooltip title={h} placement="top">
                                                <Typography
                                                    variant="subtitle2"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        cursor: 'help',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    {h}
                                                </Typography>
                                            </Tooltip>

                                            {showMapping && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'space-between' }}>
                                                    <Tooltip title={`Click to map "${h}" to a field`} placement="top">
                                                        <Chip
                                                            label={getMappedFieldLabel(h)}
                                                            size="small"
                                                            color={getMappingColor(h)}
                                                            variant={mapping[h] ? "filled" : "outlined"}
                                                            onClick={() => setMapColumnDialog({ open: true, header: h })}
                                                            clickable
                                                            sx={{
                                                                maxWidth: 120,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                flex: 1
                                                            }}
                                                        />
                                                    </Tooltip>
                                                    <Tooltip title="Delete this column" placement="top">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => setDeleteConfirm({ open: true, type: 'column', index: h, data: h })}
                                                            sx={{ minWidth: 24, width: 24, height: 24 }}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            )}
                                        </Box>
                                    </TableCell>
                                ))}

                                {(!Object.values(mapping).includes('vendorName') && (selectedVendorName || parsed.vendorName)) && (
                                    <TableCell sx={{ maxWidth: 150 }}>
                                        <Typography variant="subtitle2">Vendor Name</Typography>
                                        <Chip label="Auto-filled" size="small" color="info" variant="outlined" />
                                    </TableCell>
                                )}

                                <TableCell sx={{ width: 100 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {paginatedRows.map((entry, idx) => (
                                <TableRow key={page * rowsPerPage + idx} hover sx={{ '& > td': { py: 0.5 } }}>
                                    <TableCell sx={{ width: 50 }}>{page * rowsPerPage + idx + 1}</TableCell>

                                    {visibleHeaders.map((h) => {
                                        const mapped = mapping[h];
                                        let display = '-';

                                        if (!mapped || mapped === '' || mapped === '__extra') {
                                            display = entry.description?.jsonFields?.[h] ?? '-';
                                        } else if (mapped === 'descriptionText') {
                                            display = entry.description?.text ?? '-';
                                        } else if (mapped === 'vendorName') {
                                            display = entry.vendorName ?? '-';
                                        } else {
                                            const v = entry[mapped];
                                            display = (v === undefined || v === null || String(v) === '') ? '-' : String(v);
                                        }

                                        const displayText = display === '-' ? '-' : truncateText(display);

                                        return (
                                            <TableCell key={h} sx={{ maxWidth: 200, minWidth: 120 }}>
                                                <Tooltip title={display === '-' ? 'No data' : display} placement="top-start">
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            cursor: display !== '-' ? 'help' : 'default'
                                                        }}
                                                    >
                                                        {displayText}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                        );
                                    })}

                                    {(!Object.values(mapping).includes('vendorName') && (selectedVendorName || parsed.vendorName)) && (
                                        <TableCell sx={{ maxWidth: 150 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {entry.vendorName ?? parsed.vendorName ?? '-'}
                                            </Typography>
                                        </TableCell>
                                    )}

                                    <TableCell sx={{ width: 100 }}>
                                        <Tooltip title="Edit this row" placement="top">
                                            <IconButton
                                                size="small"
                                                onClick={() => openEdit(idx)}
                                                sx={{ minWidth: 24, width: 24, height: 24, mr: 0.5 }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete this row" placement="top">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => setDeleteConfirm({ open: true, type: 'row', index: idx, data: `Row ${page * rowsPerPage + idx + 1}` })}
                                                sx={{ minWidth: 24, width: 24, height: 24 }}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={parsed.totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Rows per page:"
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                    }
                />
            </Paper>

            {/* Column Mapping Dialog */}
            <Dialog open={mapColumnDialog.open} onClose={() => setMapColumnDialog({ open: false, header: '' })} maxWidth="sm" fullWidth>
                <Box sx={{px:3, pt:2 , pb:1}}>
                    <Typography variant="subtitle1" fontWeight={500}>
                        Map Column
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {mapColumnDialog.header}
                    </Typography>
                </Box>

                <DialogContent sx={{ pt: 1 }}>
                    <Autocomplete
                        value={schemaFields.find(field => field.key === mapping[mapColumnDialog.header]) || null}
                        onChange={(event, newValue) => {
                            if (newValue) {
                                handleColumnMapping(mapColumnDialog.header, newValue.key);
                            }
                        }}
                        options={schemaFields}
                        groupBy={(option) => {
                            if (option.key === '' || option.key === '__extra') return 'Storage Options';
                            if (option.key === 'descriptionText') return 'Description';
                            return 'Data Fields';
                        }}
                        getOptionLabel={(option) => option.label}
                        getOptionDisabled={(option) => isFieldDisabled(option.key, mapColumnDialog.header)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                // label="Search field to map"
                                size="small"
                                fullWidth
                            />
                        )}
                        renderOption={(props, option, { selected }) => (
                            <MenuItem
                                {...props}
                                sx={{
                                    py: 1,
                                    borderLeft: option.key === mapping[mapColumnDialog.header] ? 2 : 0,
                                    borderColor: 'text.primary',
                                    backgroundColor: option.key === mapping[mapColumnDialog.header] ? 'action.hover' : 'transparent'
                                }}
                            >
                                <Box sx={{ width: '100%' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                        <Typography variant="body2" fontWeight={option.key === mapping[mapColumnDialog.header] ? 500 : 400}>
                                            {option.label}
                                        </Typography>
                                        {option.key === mapping[mapColumnDialog.header] && (
                                            <Typography variant="caption" color="text.secondary">
                                                Current
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                                        {getFieldDescription(option.key)}
                                    </Typography>
                                    {isFieldDisabled(option.key, mapColumnDialog.header) && option.key !== mapping[mapColumnDialog.header] && (
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', fontStyle: 'italic' }}>
                                            Already mapped
                                        </Typography>
                                    )}
                                </Box>
                            </MenuItem>
                        )}
                    />

                    {/* Current Status - Compact */}
                    <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.primary">
                            {mapping[mapColumnDialog.header] ?
                                <>Currently mapped to: <strong>{getMappedFieldLabel(mapColumnDialog.header)}</strong></> :
                                <>Will be stored in <strong>description.jsonFields</strong></>
                            }
                        </Typography>
                    </Box>

                    {/* Compact Guidelines */}
                    <Box sx={{ mt: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                            • Unique fields can only be mapped once<br />
                            • Storage options can be reused
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 2, pb: 2, pt: 1 }}>
                    <Button
                        onClick={() => setMapColumnDialog({ open: false, header: '' })}
                        variant="text"
                        size="small"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (!mapping[mapColumnDialog.header]) {
                                handleColumnMapping(mapColumnDialog.header, '');
                            }
                            setMapColumnDialog({ open: false, header: '' });
                        }}
                        variant="contained"
                        size="small"
                        disableElevation
                    >
                        {mapping[mapColumnDialog.header] ? 'Update' : 'Store as JSON'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, type: 'row', index: -1 })}>
                <DialogTitle>
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {deleteConfirm.type === 'row'
                            ? `Are you sure you want to delete ${deleteConfirm.data}? This action cannot be undone.`
                            : `Are you sure you want to delete column "${deleteConfirm.data}"? This will remove the column from all rows.`
                        }
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm({ open: false, type: 'row', index: -1 })}>
                        Cancel
                    </Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={() => {
                            if (deleteConfirm.type === 'row') {
                                handleDeleteRow(deleteConfirm.index as number);
                            } else {
                                handleDeleteColumn(deleteConfirm.data as string);
                            }
                        }}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Row Dialog */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                        Edit Row {editRowIndex !== null ? editRowIndex + 1 : ''}
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        {/* Mapped Fields */}
                        {Object.entries(mapping)
                            .filter(([hdr, mapped]) => mapped && mapped !== '' && mapped !== '__extra')
                            .map(([hdr, mappedField]) => (
                                <Box key={hdr} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography variant="caption" sx={{ minWidth: 100, fontWeight: 500 }}>
                                        {hdr}
                                    </Typography>
                                    <TextField
                                        size="small"
                                        value={editRowData[mappedField] ?? ''}
                                        onChange={(e) => setEditRowData(prev => ({ ...prev, [mappedField]: e.target.value }))}
                                        sx={{ flex: 1 }}
                                        placeholder={`Enter ${mappedField}`}
                                    />
                                </Box>
                            ))}

                        {/* Description Text */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ minWidth: 100, fontWeight: 500 }}>
                                Description Text
                            </Typography>
                            <TextField
                                size="small"
                                value={editRowData.description?.text ?? ''}
                                onChange={(e) => setEditRowData(prev => ({
                                    ...prev,
                                    description: { ...prev.description, text: e.target.value }
                                }))}
                                sx={{ flex: 1 }}
                                placeholder="Enter description"
                            />
                        </Box>

                        {/* JSON Fields */}
                        {Object.keys(editRowData.description?.jsonFields ?? {}).length > 0 && (
                            <Box>
                                <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
                                    Additional Fields
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {Object.keys(editRowData.description?.jsonFields ?? {}).map(k => (
                                        <Box key={k} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="caption" sx={{ minWidth: 80, color: 'text.secondary' }}>
                                                {k}
                                            </Typography>
                                            <TextField
                                                size="small"
                                                value={editRowData.description?.jsonFields?.[k] ?? ''}
                                                onChange={(e) => setEditRowData(prev => ({
                                                    ...prev,
                                                    description: {
                                                        ...prev.description,
                                                        jsonFields: {
                                                            ...(prev.description?.jsonFields || {}),
                                                            [k]: e.target.value
                                                        }
                                                    }
                                                }))}
                                                sx={{ flex: 1 }}
                                                placeholder={`Enter ${k}`}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 2, pb: 2, pt: 1 }}>
                    <Button
                        onClick={() => setEditOpen(false)}
                        size="small"
                        variant="text"
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={saveEdit}
                        size="small"
                        disableElevation
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DataPreviewTable;